/**
 * Timing, space, and movement constants.
 *
 * The two clocks: the game clock resolves everything measured in ticks (combat,
 * skills, timers). The movement clock advances continuous position between them.
 * See docs/PLAN.md section 1.
 */

/** Game tick length in milliseconds. Every combat and skill timing derives from this. */
export const TICK_MS = 600;

/** Movement simulation step in milliseconds. 12 steps per game tick. */
export const MOVE_STEP_MS = 50;

/** Movement steps per game tick. */
export const STEPS_PER_TICK = TICK_MS / MOVE_STEP_MS;

/** How often quantized positions go out to nearby clients. Every other movement step. */
export const POSITION_BROADCAST_MS = 100;

/**
 * Movement speeds in tiles per second, matching one tile per tick walking and two
 * tiles per tick running. Identical for every player.
 */
export const WALK_SPEED = 1000 / TICK_MS; // 1.6667 tiles/sec
export const RUN_SPEED = 2000 / TICK_MS; // 3.3333 tiles/sec

/** Collision radius of a player or NPC, in tiles. */
export const ENTITY_RADIUS = 0.35;

/**
 * How much ground one chunk holds, in tiles.
 *
 * The unit the world is stored in, streamed in and drawn in. One number rather
 * than three, because three would drift.
 */
export const CHUNK_TILES = 64;

/** A zone is the unit of interest management. Events publish per zone, not per player. */
export const ZONE_SIZE = 8;
export const ZONE_SHIFT = 3;

/** Half-width of a player's view, in tiles. Matches the OSRS 104x104 viewport. */
export const VIEW_RADIUS_TILES = 52;

/** Zones a player subscribes to in each direction. */
export const VIEW_RADIUS_ZONES = Math.ceil(VIEW_RADIUS_TILES / ZONE_SIZE);

/**
 * How much further than that somebody is kept once they are already known.
 *
 * Coming into view and going out of it are deliberately not the same line. With
 * one line, two people walking side by side along it appear and disappear from
 * each other every few steps, and anything that steps over it and back seems to
 * flicker. Keeping what is already known for a band beyond the line means a
 * border is crossed once rather than argued with, and it costs one ring of zones.
 *
 * The band also decides how far position updates travel, which matters more than
 * it sounds: somebody known but not updated is standing still on screen, and
 * arrives at their real position in one jump the moment they are updated again.
 * That jump is what reads as a monster teleporting.
 */
export const KEEP_RADIUS_ZONES = VIEW_RADIUS_ZONES + 1;

/** Vertical levels, ground floor upward. */
export const MAX_PLANE = 3;

/** Positions travel the wire quantized to this fraction of a tile. */
export const POSITION_PRECISION = 64;

/** Run energy, stored as 0 to 10000 so drain can be sub-integer without float drift. */
export const MAX_RUN_ENERGY = 10_000;

/** Hard cap on experience in a single skill. */
export const MAX_EXPERIENCE = 200_000_000;

/** Highest reachable level in a normal skill. */
export const MAX_LEVEL = 99;

/** Number of inventory slots. */
export const INVENTORY_SIZE = 28;

/** Number of equipment slots. */
export const EQUIPMENT_SLOTS = 11;

/**
 * The height the sea sits at. Ground below this is underwater and is drawn as
 * such; ground above it is land. Kept here because the renderer, the region
 * generator, and the world editor all have to agree on where the coast is.
 */
export const SEA_LEVEL = 0;

/** How deep the open sea is drawn, in tiles below the surface. */
export const SEA_FLOOR = -3.6;

/** Height the editor lifts ground to when it turns sea back into coast. */
export const SHORE_HEIGHT = 0.35;

/** Ground within this much of the surface is beach rather than grass. */
export const SHORE_BAND = 0.55;
